import { computed, inject } from '@angular/core';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, forkJoin, Observable, pipe } from 'rxjs';
import { catchError, concatMap, finalize, map, switchMap, tap } from 'rxjs/operators';
import { StoriesApiService } from '@app/users-area/api/stories.api.service';
import { StoryDetail } from '@app/utils/models/story-detail.model';
import { StoryFilter } from '@app/utils/models/story-filter.model';
import { Story } from '@app/utils/models/story.model';
import { SnackBarService } from '@shared/domain/snack-bar.service';
import { setBookmarkedStory, setStoryBookmark, setStoryDetailBookmark } from './members-area-stories-reducers';

// One list per story filter the page loads, each feeding its own section.
interface MembersAreaStoriesState {
  bookmarkedStories$$: Story[];
  eventStories$$: Story[];
  hasStoryDetailLoadError$$: boolean;
  hotelStories$$: Story[];
  insiderStories$$: Story[];
  isLoading$$: boolean;
  storyDetail$$: StoryDetail | null;
  tripNearByStories$$: Story[];
  tripStories$$: Story[];
}

const INITIAL_STATE: MembersAreaStoriesState = {
  bookmarkedStories$$: [],
  eventStories$$: [],
  hasStoryDetailLoadError$$: false,
  hotelStories$$: [],
  insiderStories$$: [],
  isLoading$$: false,
  storyDetail$$: null,
  tripNearByStories$$: [],
  tripStories$$: [],
};

/**
 * Stories shown in the members area, and the detail of the one opened in the story details dialog.
 */
export const MembersAreaStoriesStore = signalStore(
  { providedIn: 'root' },
  withState<MembersAreaStoriesState>(INITIAL_STATE),

  withComputed(store => {
    // Every story card the page holds, from all its lists. The open story is a detail, not a card, so it is not one.
    const stories$$ = computed(() => [
      ...store.bookmarkedStories$$(),
      ...store.eventStories$$(),
      ...store.hotelStories$$(),
      ...store.insiderStories$$(),
      ...store.tripNearByStories$$(),
      ...store.tripStories$$(),
    ]);
    return {
      // Loading with no stories to show yet. A reload keeps the stories already shown, like the trips page.
      isInitialLoad$$: computed(() => store.isLoading$$() && !stories$$().length),
      stories$$,
    };
  }),

  withMethods(
    (
      store,
      // DI
      snackBarService = inject(SnackBarService),
      storiesApiService = inject(StoriesApiService),
    ) => {
      // The story of a card, from any list, found by its slug.
      const findStory = (slug: string): Story | undefined => store.stories$$().find(story => story.slug === slug);

      // Sets a story's bookmark everywhere the store holds it: the open story, every list and the saved stories.
      const updateStoryBookmark = (slug: string, isBookmarked: boolean, story: Story | undefined): void =>
        patchState(store, state => ({
          bookmarkedStories$$: setBookmarkedStory(state.bookmarkedStories$$, slug, isBookmarked, story),
          eventStories$$: setStoryBookmark(state.eventStories$$, slug, isBookmarked),
          hotelStories$$: setStoryBookmark(state.hotelStories$$, slug, isBookmarked),
          insiderStories$$: setStoryBookmark(state.insiderStories$$, slug, isBookmarked),
          storyDetail$$: setStoryDetailBookmark(state.storyDetail$$, slug, isBookmarked),
          tripNearByStories$$: setStoryBookmark(state.tripNearByStories$$, slug, isBookmarked),
          tripStories$$: setStoryBookmark(state.tripStories$$, slug, isBookmarked),
        }));

      return {
        // Clears the previous detail first, so the story never shows another one while it loads. A failed load is
        // only flagged: each view words it its own way (the dialog closes with a toast, the story page offers a retry).
        getStoryDetail: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { hasStoryDetailLoadError$$: false, storyDetail$$: null })),
            switchMap(slug =>
              storiesApiService.getStoryDetail$(slug).pipe(
                tap(storyDetail => patchState(store, { storyDetail$$: storyDetail })),
                catchError(() => {
                  patchState(store, { hasStoryDetailLoadError$$: true });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        // The endpoint takes a single filter, so every section's filter is requested on its own, in parallel.
        loadStories$(): Observable<void> {
          patchState(store, { isLoading$$: true });
          return forkJoin([
            storiesApiService.getStoriesByFilter$(StoryFilter.IsBookmarked),
            storiesApiService.getStoriesByFilter$(StoryFilter.Event),
            storiesApiService.getStoriesByFilter$(StoryFilter.Hotel),
            storiesApiService.getStoriesByFilter$(StoryFilter.Insider),
            storiesApiService.getStoriesByFilter$(StoryFilter.Trip),
            storiesApiService.getStoriesByFilter$(StoryFilter.TripNearBy),
          ]).pipe(
            tap(([bookmarkedStories, eventStories, hotelStories, insiderStories, tripStories, tripNearByStories]) =>
              patchState(store, {
                bookmarkedStories$$: bookmarkedStories,
                eventStories$$: eventStories,
                hotelStories$$: hotelStories,
                insiderStories$$: insiderStories,
                tripNearByStories$$: tripNearByStories,
                tripStories$$: tripStories,
              }),
            ),
            map(() => undefined),
            catchError(() => {
              snackBarService.openToast({
                closable: true,
                feedback: 'danger',
                messageKey: marker('errors.genericRequestRetry'),
              });
              return EMPTY;
            }),
            finalize(() => patchState(store, { isLoading$$: false })),
          );
        },

        // Optimistic: the story's bookmark flips at once, in the open story, in every list and in the saved stories,
        // and the BE call runs behind it. The calls queue, so fast clicks reach the BE in click order; a failed call
        // flips the bookmark back. The saved stories are not loaded again: they reload with the page.
        toggleStoryBookmark: rxMethod<Pick<Story, 'isBookmarked' | 'slug'>>(
          pipe(
            map(({ isBookmarked, slug }) => ({ isBookmarked: !isBookmarked, slug, story: findStory(slug) })),
            tap(({ isBookmarked, slug, story }) => updateStoryBookmark(slug, isBookmarked, story)),
            concatMap(({ isBookmarked, slug, story }) =>
              (isBookmarked ? storiesApiService.bookmarkStory$(slug) : storiesApiService.unbookmarkStory$(slug)).pipe(
                catchError(() => {
                  updateStoryBookmark(slug, !isBookmarked, story);
                  snackBarService.openToast({
                    closable: true,
                    feedback: 'danger',
                    messageKey: marker('errors.genericRequestRetry'),
                  });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        // The stories are the member's own (bookmarks, trips near their city), so they go on logout.
        unloadStories(): void {
          patchState(store, INITIAL_STATE);
        },
      };
    },
  ),
);
